import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router-dom'
import {
  IonContent,
  IonPage,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react'
import { App } from '@capacitor/app'
import { PluginListenerHandle } from '@capacitor/core'
import queryString from 'query-string'
import { Swiper, SwiperSlide } from 'swiper/react'

import { ContestSlide } from '@components/organisms/ContestDetails/ContestSlide'
import { ContestDetailsListHeader } from '@components/organisms/ContestDetailsListHeader'
import { Loading } from '@/components/atoms/Loading'
import { Popover } from '@/components/molecules/Popover'

import { appConfig } from '@/services/config'
import { useContestDetails } from '@/lib/hooks/useContestDetails'
import { useUserInfo } from '@/lib/hooks/useUserInfo'
import { TrackingEvent } from '@/lib/tracking'

export const ContestDetailsListPage: FC<RouteComponentProps> = ({
  match,
  history,
}: RouteComponentProps<{ contestId: UUID; videoId: UUID }>) => {
  const {
    location: { search = '' },
  } = history
  const { page = '' } = queryString.parse(search) as { page: string }
  const { channel = '' } = queryString.parse(search) as { channel: string }
  const [slideIncrement, setSlideIncrement] = useState(0)
  const [isActive, setIsActive] = useState<boolean>(true)
  const [activeSliderIndex, setActiveSliderIndex] = useState<number>(0)
  const [appStateChangeHandler, setAppStateChangeHandler] =
    useState<PluginListenerHandle | null>(null)
  const { data: authUser } = useUserInfo()
  const [isViewActive, setViewActive] = useState(false)
  const [isPopoverVisible, setIsPopoverVisible] = useState(false)
  const [wasPopoverVisibleOnce, setWasPopoverVisibleOnce] = useState(false)
  const { t } = useTranslation('common')
  const [activities, setActivities] = useState([])
  const {
    params: { contestId, videoId },
  } = match

  const {
    contestDetails,
    isLoading: isLoadingContestDetails,
    hasNextPage,
    fetchNextPage,
  } = useContestDetails({
    contestId,
    limit: appConfig.contestDetailsList.limit,
    filter: videoId,
    options: {
      refetchOnMount: true,
    },
  })

  useEffect(() => {
    setActivities(contestDetails?.participants)
  }, [contestDetails?.participants])

  const onSliderMove = (e) => {
    if (
      activities.length === 1 &&
      !wasPopoverVisibleOnce &&
      Math.abs(e.touches.diff) > 20
    ) {
      setIsPopoverVisible(true)
      setWasPopoverVisibleOnce(true)
    }
  }

  const onSlideChange = (e) => {
    setSlideIncrement(
      e.previousIndex < e.activeIndex ? slideIncrement + 1 : slideIncrement - 1
    )
    setActiveSliderIndex(e.activeIndex)
    window.history.replaceState(
      '',
      '',
      `/contest/${contestId}/video/${activities?.[e.activeIndex].video.id}`
    )
  }

  const onSlideChangeTransitionEnd = () => {
    if (hasNextPage && slideIncrement === activities.length - 1) {
      fetchNextPage()
    }
  }

  useIonViewWillEnter(() => {
    setAppStateChangeHandler(
      App.addListener('appStateChange', ({ isActive }) => setIsActive(isActive))
    )
    setViewActive(true)
  })

  useIonViewWillLeave(() => {
    setViewActive(false)

    if (appStateChangeHandler) {
      appStateChangeHandler.remove()
    }
  })

  return (
    <IonPage className="mx-auto max-w-[40rem]">
      <ContestDetailsListHeader
        contestId={contestId}
        contestName={contestDetails?.name}
        contestStatus={contestDetails?.status}
      />
      <IonContent scrollY={false} fullscreen>
        <div className="w-full h-full flex content-center">
          {!isLoadingContestDetails ? (
            <div className={'w-full h-full relative'}>
              <Popover
                isOpen={isPopoverVisible}
                onDismiss={() => setIsPopoverVisible(false)}
              >
                <strong>{t('contest.joinContestsHint')}</strong>
              </Popover>
              <Swiper
                onSliderMove={onSliderMove}
                direction={'vertical'}
                observer={true}
                className={'w-full h-full'}
                onSlideChange={onSlideChange}
                onSlideChangeTransitionEnd={onSlideChangeTransitionEnd}
              >
                {activities?.map((activity, index) => (
                  <SwiperSlide key={contestId + activity.user.id}>
                    <ContestSlide
                      key={`slide-${contestId}${activity.user.id}`}
                      isActive={isActive && isViewActive}
                      participant={activity}
                      authUserId={authUser?.id}
                      activeSliderIndex={activeSliderIndex}
                      slideIndex={index}
                      contestDetails={contestDetails}
                      history={history}
                      channel={channel}
                      page={page}
                      tracking={{
                        eventName: activity?.id
                          ? TrackingEvent.SubmissionViewed
                          : TrackingEvent.ChallengeCreatorVideoViewed,

                        eventProperties: {
                          challengeId: contestId,
                          challengeName: contestDetails?.name,
                          submissionId: activity?.id,
                        },
                      }}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            <Loading />
          )}
        </div>
      </IonContent>
    </IonPage>
  )
}
